// Audited owner-report mutations introduced by 228. No Storage removal, client
// Activity INSERT or create/replay behavior belongs here. Each RPC is atomic;
// a transport failure may have committed and must never be presented as success.
import {fireActivityChangeEvent} from './activityApi.js';
import {COMMENT_CHANGE_EVENT} from './commentsApi.js';
import type {SupabaseClient} from './supabaseTypes.js';

export type OwnerReportMutation = {ok: true; changed: boolean; event_id?: string};
export type OwnerReportRow = {
  id: string;
  owner_profile_id?: string | null;
  date?: string | null;
  team_member?: string | null;
  deleted_at?: string | null;
  [key: string]: unknown;
};
export type OwnerFuelingRow = OwnerReportRow & {
  equipment_id: string;
  gallons?: number | string | null;
  hours_reading?: number | string | null;
  km_reading?: number | string | null;
  comments?: string | null;
  def_gallons?: number | string | null;
  photos?: unknown;
};
export type OwnerFuelSupplyRow = OwnerReportRow & {
  gallons: number | string;
  fuel_type?: string | null;
  destination: string;
  notes?: string | null;
};

const REFUSALS: Record<string, string> = {
  not_found: 'This report could not be found. Reload the list.',
  no_fueling: 'This fueling report could not be found. Reload the list.',
  deleted: 'This report was deleted. Reload the list.',
  bad_args: 'This report could not be identified. Reload the list.',
};

export function requireOwnerReportMutation(data: unknown): OwnerReportMutation {
  const value = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  if (value?.ok !== true || typeof value.changed !== 'boolean') {
    const reason = typeof value?.reason === 'string' ? value.reason : '';
    throw new Error(
      REFUSALS[reason] || 'The server did not confirm this change. Reload to check the report before trying again.',
    );
  }
  return value as OwnerReportMutation;
}

type Reply = {data: unknown; error: {message?: string; code?: string} | null};
async function mutation(send: () => PromiseLike<Reply>): Promise<OwnerReportMutation> {
  let response: Reply;
  try {
    response = await send();
  } catch {
    throw new Error('Could not reach the server. This change may have saved; reload to check before trying again.');
  }
  if (response.error) {
    if (response.error.code === '42501') throw new Error('You do not have permission to change this report.');
    if (!response.error.code)
      throw new Error(
        'Could not confirm the change. Reload to check before trying again. ' + (response.error.message || ''),
      );
    throw new Error(response.error.message || 'The report could not be changed.');
  }
  return requireOwnerReportMutation(response.data);
}

export async function deleteOwnFueling(sb: SupabaseClient, row: Pick<OwnerFuelingRow, 'id' | 'equipment_id'>) {
  const result = await mutation(() => sb.rpc('delete_equipment_fueling', {p_id: row.id}));
  if (result.changed) fireActivityChangeEvent('equipment.item', row.equipment_id);
  return result;
}
export async function updateOwnerFuelSupply(sb: SupabaseClient, id: string, patch: Record<string, unknown>) {
  const result = await mutation(() => sb.rpc('update_fuel_supply', {p_id: id, p_patch: patch}));
  if (result.changed) fireActivityChangeEvent('equipment.fuel_supply', id);
  return result;
}
export async function deleteOwnFuelSupply(sb: SupabaseClient, id: string) {
  const result = await mutation(() => sb.rpc('delete_fuel_supply', {p_id: id}));
  if (result.changed) fireActivityChangeEvent('equipment.fuel_supply', id);
  return result;
}
export async function restoreOwnerFueling(sb: SupabaseClient, row: Pick<OwnerFuelingRow, 'id' | 'equipment_id'>) {
  const result = await mutation(() => sb.rpc('restore_equipment_fueling', {p_id: row.id}));
  if (result.changed) fireActivityChangeEvent('equipment.item', row.equipment_id);
  return result;
}
export async function restoreOwnerFuelSupply(sb: SupabaseClient, id: string) {
  const result = await mutation(() => sb.rpc('restore_fuel_supply', {p_id: id}));
  if (result.changed) fireActivityChangeEvent('equipment.fuel_supply', id);
  return result;
}
export async function restoreOwnerCattleLog(sb: SupabaseClient, id: string) {
  const result = await mutation(() => sb.rpc('restore_cattle_log_entry', {p_id: id}));
  if (result.changed) {
    fireActivityChangeEvent('cattle.log', 'cattle-log');
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(COMMENT_CHANGE_EVENT));
  }
  return result;
}
